import React, { useState } from 'react'
import Browse from './Browse'
import Journal from './Journal'
import Dropdown from './Dropdown'
import * as H from 'history'
import { NavItemContextProvider } from './NavItemContext'
import { useNavigationContext } from '#src/components/Header/StandardHeader/NavigationContext'
import { NavLinkProps } from 'react-router-dom'

interface NavItemProps extends Omit<NavLinkProps, 'to'> {
  to?:
    | H.LocationDescriptor<H.LocationState>
    | ((location: H.Location<H.LocationState>) => H.LocationDescriptor<H.LocationState>)
  desktopOnly?: boolean
  menuPosition?: 'left' | 'right'
  menuOptions?: React.ReactNode
  onStaticAction?: () => void
  testId?: string
  onActiveChanged?: (isActive: boolean) => void
}

export interface ContainedNavItemProps extends NavItemProps {
  onNavItemClicked: () => void
  onNavItemClickedAway: () => void
  isNavItemActive: boolean
}

type NavItemContainerChildrenFunction = (result: {
  onNavItemClicked: () => void
  onNavItemClickedAway: () => void
  isNavItemActive: boolean
}) => React.ReactNode

interface NavItemContainerProps {
  children: NavItemContainerChildrenFunction
  delay?: string
}

const NavItemContainer: React.FC<NavItemContainerProps> = ({ children }) => {
  const navContext = useNavigationContext()
  const [isNavItemActive, setIsNavItemActive] = useState(false)

  const onNavItemClicked = () => {
    if (isNavItemActive) {
      navContext?.onToggleIsVeilVisible(false)
      setIsNavItemActive(false)
    } else {
      navContext?.onToggleIsVeilVisible(true)
      setIsNavItemActive(true)
    }
  }

  const onNavItemClickedAway = () => {
    setIsNavItemActive(false)
  }

  if (children && typeof children === 'function') {
    return (
      <NavItemContextProvider
        value={{
          onNavItemSelected: () => {
            // We've navigated to something, close all the menus
            navContext?.onToggleIsVeilVisible(false)
            setIsNavItemActive(false)
          }
        }}
      >
        {children({
          onNavItemClicked,
          onNavItemClickedAway,
          isNavItemActive
        })}
      </NavItemContextProvider>
    )
  }

  return null
}

const NavItemBrowseWithContainer: React.FC<NavItemProps> = ({ ...props }) => {
  return (
    <NavItemContainer>
      {({ ...containerProps }) => <Browse {...containerProps} {...props} />}
    </NavItemContainer>
  )
}

const NavItemJournalWithContainer: React.FC<NavItemProps> = ({ ...props }) => {
  return (
    <NavItemContainer>
      {({ ...containerProps }) => <Journal {...containerProps} {...props} />}
    </NavItemContainer>
  )
}

const NavItemWithContainer: React.FC<NavItemProps> = ({ desktopOnly, ...props }) => {
  if (desktopOnly) {
    return null
  }

  return (
    <NavItemContainer>
      {({ ...containerProps }) => <Dropdown {...containerProps} {...props} />}
    </NavItemContainer>
  )
}

export {
  NavItemBrowseWithContainer as NavItemBrowse,
  NavItemJournalWithContainer as NavItemJournal,
  NavItemWithContainer as NavItem
}
